import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import RouteIcon from '@mui/icons-material/Route';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SendIcon from '@mui/icons-material/Send';
import { driverLogService, truckService } from '../api/api';
import { useAuth } from '../auth/AuthContext';
import { getVehicleCategoryId } from '../utils/vehicleCategories';
import { confirmOdometerUpdate } from '../utils/odometer';
import { formatPortalDate } from '../utils/dateFormat';

const FLEET_GROUPS = [
  '5 Ton Trucks',
  'Trailers',
  'Forklifts & Skid Steers',
  'Vans & Service Vehicles',
  'Parts Trucks',
  'Mid-Size Box Trucks',
  'Tractors',
  'Other Vehicles',
];

function fleetGroup(truck) {
  const category = getVehicleCategoryId(truck);
  const searchable = [truck?.name, truck?.vehicle_type, truck?.folder, truck?.make, truck?.model].filter(Boolean).join(' ').toLowerCase();
  if (category === 'fiveTonnes') return '5 Ton Trucks';
  if (category === 'trailers') return 'Trailers';
  if (category === 'forklifts' || category === 'skidsteer') return 'Forklifts & Skid Steers';
  if (category === 'vans' || searchable.includes('service vehicle') || searchable.includes('service truck')) return 'Vans & Service Vehicles';
  if (category === 'partsTruck') return 'Parts Trucks';
  if (category === 'midSizeBox') return 'Mid-Size Box Trucks';
  if (category === 'tractors') return 'Tractors';
  return 'Other Vehicles';
}

function vehicleLabel(truck) {
  const name = truck?.name || [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' ') || `Vehicle #${truck?.id}`;
  return truck?.license_plate ? `${name} • ${truck.license_plate}` : name;
}

function vehicleSecondaryLabel(truck) {
  return [
    [truck?.year, truck?.make, truck?.model].filter(Boolean).join(' '),
    truck?.license_plate ? `Plate: ${truck.license_plate}` : '',
    truck?.odometer != null ? `Odometer: ${Number(truck.odometer).toLocaleString()} km` : '',
    truck?.vehicle_type || truck?.folder || '',
  ].filter(Boolean).join(' • ') || 'No additional vehicle details';
}

function tripEligible(truck) {
  const category = getVehicleCategoryId(truck);
  if (['vans', 'trailers', 'tractors', 'forklifts', 'skidsteer', 'partsTruck'].includes(category)) return true;
  const searchable = [truck?.name, truck?.vehicle_type, truck?.folder, truck?.make, truck?.model].filter(Boolean).join(' ').toLowerCase();
  return ['service vehicle', 'service truck', 'van', 'minivan', 'tractor', 'trailer', 'forklift', 'skid steer', 'skidsteer']
    .some((term) => searchable.includes(term));
}

function groupedVehicles(vehicles) {
  const groups = new Map(FLEET_GROUPS.map((group) => [group, []]));
  vehicles.forEach((truck) => groups.get(fleetGroup(truck)).push(truck));
  groups.forEach((items) => items.sort((a, b) => vehicleLabel(a).localeCompare(vehicleLabel(b))));
  return FLEET_GROUPS.map((label) => ({ label, vehicles: groups.get(label) })).filter((group) => group.vehicles.length);
}

function today() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

function localDateTime() {
  const date = new Date();
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
}

function timeValue(dateTimeValue) {
  return String(dateTimeValue || '').match(/T(\d{2}:\d{2})/)?.[1] || '';
}

function dateTimeForTripDate(tripDate, dateTimeValue) {
  if (!tripDate) return '';
  return `${tripDate}T${timeValue(dateTimeValue) || timeValue(localDateTime())}`;
}

function formatDate(value, includeTime = false) {
  if (!value) return '—';
  const date = new Date(includeTime ? value : `${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return formatPortalDate(date, { includeTime });
}

function VehicleSelect({ vehicles, value, onChange, label = 'Vehicle' }) {
  const groups = useMemo(() => groupedVehicles(vehicles), [vehicles]);
  return (
    <FormControl fullWidth required>
      <InputLabel>{label}</InputLabel>
      <Select
        value={value}
        label={label}
        onChange={(event) => onChange(event.target.value)}
        renderValue={(selectedValue) => {
          const selected = vehicles.find((truck) => String(truck.id) === String(selectedValue));
          if (!selected) return 'Select a vehicle';
          return (
            <Box sx={{ minWidth: 0, py: 0.25 }}>
              <Typography component="span" sx={{ display: 'block', fontWeight: 900, lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {vehicleLabel(selected)}
              </Typography>
              <Typography component="span" variant="caption" sx={{ display: 'block', color: 'text.secondary', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {selected.odometer != null ? `${Number(selected.odometer).toLocaleString()} km` : 'Odometer not recorded'}
              </Typography>
            </Box>
          );
        }}
        MenuProps={{
          PaperProps: {
            sx: { maxHeight: 480, width: { xs: 'calc(100vw - 32px)', sm: 520 }, maxWidth: 'calc(100vw - 32px)' },
          },
        }}
      >
        {groups.flatMap((group) => [
          <ListSubheader key={`group-${group.label}`} sx={{ fontWeight: 900, bgcolor: '#dfeefb', color: '#001f3f', lineHeight: '42px', fontSize: '0.82rem', borderBottom: '1px solid #c8dceb' }}>
            {group.label} ({group.vehicles.length})
          </ListSubheader>,
          ...group.vehicles.map((truck) => (
            <MenuItem key={truck.id} value={String(truck.id)} sx={{ px: 2.5, py: 1.25, minHeight: 62, whiteSpace: 'normal', borderBottom: '1px solid #edf2f7', alignItems: 'flex-start' }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 900, color: '#001f3f', lineHeight: 1.25 }}>
                  {truck.name || `Vehicle #${truck.id}`}
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.4, color: '#536578', lineHeight: 1.35, overflowWrap: 'anywhere' }}>
                  {vehicleSecondaryLabel(truck)}
                </Typography>
              </Box>
            </MenuItem>
          )),
        ])}
      </Select>
    </FormControl>
  );
}

function RecordCard({ title, subtitle, details, status, onDelete }) {
  return (
    <Card variant="outlined" sx={{ borderRadius: 3, height: '100%' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography fontWeight={900}>{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
          {status && <Chip size="small" label={status} color={status === 'Draft' ? 'warning' : 'success'} />}
        </Box>
        <Stack spacing={1} sx={{ mt: 2 }}>
          {details.map(({ label, value }) => (
            <Box key={label} sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'space-between', gap: { xs: 0.2, sm: 2 }, pb: 0.7, borderBottom: '1px solid #eef2f6' }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="body2" fontWeight={700} textAlign={{ xs: 'left', sm: 'right' }} sx={{ overflowWrap: 'anywhere' }}>{value || '—'}</Typography>
            </Box>
          ))}
        </Stack>
        {onDelete && (
          <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} onClick={onDelete} fullWidth sx={{ mt: 2, minHeight: 44 }}>
            Delete Record
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function DriverOperationalLogs({ type }) {
  const { user, hasFullAccess } = useAuth();
  const isFuel = type === 'fuel';
  const [vehicles, setVehicles] = useState([]);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [toast, setToast] = useState({ open: false, severity: 'success', message: '' });
  const [activeTrip, setActiveTrip] = useState(null);
  const [tripMode, setTripMode] = useState('beginning');
  const [fuelForm, setFuelForm] = useState({
    truck_id: '', fueled_at: localDateTime(), odometer: '', quantity_litres: '', total_cost: '', fuel_type: 'Diesel', fuel_source: 'biomed', station: 'Biomed', notes: '',
  });
  const [tripForm, setTripForm] = useState({
    truck_id: '', other_driver: '', trip_date: today(), start_time: localDateTime(), beginning_odometer: '', title: 'Trip', ending_odometer: '', notes: '',
  });

  const availableVehicles = useMemo(
    () => (isFuel ? vehicles : vehicles.filter(tripEligible)),
    [isFuel, vehicles],
  );
  const activeLog = activeTrip?.log;
  const totalTrip = Math.max(0, Number(tripForm.ending_odometer || 0) - Number(activeLog?.beginning_odometer || 0));

  const selectFuelVehicle = (value) => {
    const selected = vehicles.find((truck) => String(truck.id) === String(value));
    setFuelForm((form) => ({
      ...form,
      truck_id: value,
      odometer: selected?.odometer ?? '',
    }));
  };

  const selectTripVehicle = (value) => {
    setTripForm((form) => ({
      ...form,
      truck_id: value,
      beginning_odometer: '',
    }));
  };

  const load = async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');
      const [vehiclesResponse, recordsResponse, activeResponse] = await Promise.all([
        truckService.getAll(),
        isFuel
          ? driverLogService.getFuelLogs({ all: hasFullAccess })
          : driverLogService.getTripLogs({ all: hasFullAccess }),
        isFuel ? Promise.resolve({ data: null }) : driverLogService.getActiveTrip(),
      ]);
      const vehicleRows = vehiclesResponse.data?.data || vehiclesResponse.data || [];
      setVehicles(Array.isArray(vehicleRows) ? vehicleRows : []);
      setRecords(Array.isArray(recordsResponse.data) ? recordsResponse.data : []);
      if (!isFuel) {
        const nextActive = activeResponse.data || null;
        setActiveTrip(nextActive);
        if (nextActive) {
          setTripMode('ending');
          setTripForm((form) => ({
            ...form,
            truck_id: String(nextActive.log.truck_id),
            trip_date: nextActive.log.trip_date,
            start_time: nextActive.log.started_at || form.start_time,
            beginning_odometer: nextActive.log.beginning_odometer,
            title: nextActive.log.title || 'Trip',
            ending_odometer: nextActive.log.ending_odometer ?? '',
            notes: nextActive.log.notes || '',
          }));
        }
      }
    } catch (loadError) {
      setError(loadError?.response?.data?.error || loadError?.message || `Failed to load ${isFuel ? 'fuel' : 'trip'} records.`);
    } finally {
      if (!quiet) setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const refreshQuietly = () => {
      if (document.visibilityState === 'visible') load({ quiet: true });
    };
    const interval = window.setInterval(refreshQuietly, 10_000);
    window.addEventListener('focus', refreshQuietly);
    window.addEventListener('dataUpdated', refreshQuietly);
    document.addEventListener('visibilitychange', refreshQuietly);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', refreshQuietly);
      window.removeEventListener('dataUpdated', refreshQuietly);
      document.removeEventListener('visibilitychange', refreshQuietly);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const saveFuel = async () => {
    try {
      setSaving(true);
      setError('');
      const payload = {
        ...fuelForm,
        station: fuelForm.fuel_source === 'biomed' ? 'Biomed' : fuelForm.station,
        total_cost: fuelForm.fuel_source === 'outside' ? fuelForm.total_cost : null,
      };
      if (payload.fuel_source === 'outside' && payload.total_cost === '') {
        setError('Total cost is required for fuel filled outside Biomed.');
        setSaving(false);
        return;
      }
      await driverLogService.createFuelLog(payload);
      setFuelForm({ truck_id: '', fueled_at: localDateTime(), odometer: '', quantity_litres: '', total_cost: '', fuel_type: 'Diesel', fuel_source: 'biomed', station: 'Biomed', notes: '' });
      setToast({ open: true, severity: 'success', message: 'Fuel record saved successfully.' });
      window.dispatchEvent(new Event('dataUpdated'));
      await load();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Failed to save fuel record.');
    } finally {
      setSaving(false);
    }
  };

  const beginTrip = async () => {
    try {
      const selectedVehicle = vehicles.find((truck) => String(truck.id) === String(tripForm.truck_id));
      const beginningOdometer = Number(tripForm.beginning_odometer);
      if (!selectedVehicle) return setError('Vehicle is required.');
      if (!Number.isFinite(beginningOdometer) || beginningOdometer < 0) return setError('Beginning odometer is required and must be a non-negative number.');
      const startedAt = new Date(tripForm.start_time);
      if (Number.isNaN(startedAt.getTime())) return setError('A valid trip date and start time are required.');
      setSaving(true);
      setError('');
      await driverLogService.beginTrip({
        truck_id: tripForm.truck_id,
        other_driver: tripForm.other_driver,
        trip_date: tripForm.trip_date,
        start_time: startedAt.toISOString(),
        beginning_odometer: beginningOdometer,
        notes: tripForm.notes,
      });
      setToast({ open: true, severity: 'success', message: 'Beginning of trip saved successfully.' });
      window.dispatchEvent(new Event('dataUpdated'));
      await load();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Failed to save beginning of trip.');
    } finally {
      setSaving(false);
    }
  };

  const saveTrip = async (status) => {
    try {
      if (!activeLog) return setError('No active trip was found. Save Beginning of Trip first.');
      const selectedVehicle = activeTrip.truck;
      const beginningOdometer = Number(activeLog.beginning_odometer);
      if (!tripForm.title.trim()) return setError('Trip title is required.');
      if (status === 'draft') {
        const draftEnding = tripForm.ending_odometer === '' ? null : Number(tripForm.ending_odometer);
        if (draftEnding != null && (!Number.isFinite(draftEnding) || draftEnding < beginningOdometer)) return setError('Ending odometer cannot be lower than beginning odometer.');
        setSaving(true);
        setError('');
        await driverLogService.saveTripDraft(activeLog.id, {
          title: tripForm.title,
          ending_odometer: draftEnding,
          notes: tripForm.notes,
        });
        setToast({ open: true, severity: 'success', message: 'Ending-of-trip draft saved.' });
        await load();
        return;
      }
      const endingOdometer = Number(tripForm.ending_odometer);
      if (tripForm.ending_odometer === '' || !Number.isFinite(endingOdometer) || endingOdometer < beginningOdometer) return setError('Ending odometer cannot be lower than beginning odometer.');
      let updateVehicleOdometer = false;
      if (Number.isFinite(beginningOdometer) && Number.isFinite(endingOdometer) && endingOdometer > beginningOdometer) {
        updateVehicleOdometer = await confirmOdometerUpdate({
          currentValue: selectedVehicle?.odometer ?? beginningOdometer,
          nextValue: endingOdometer,
          context: `save this trip log and update ${selectedVehicle ? vehicleLabel(selectedVehicle) : 'the selected vehicle'} to the ending odometer`,
        });
        if (!updateVehicleOdometer) return;
      }
      setSaving(true);
      setError('');
      await driverLogService.completeTrip(activeLog.id, {
        title: tripForm.title,
        ending_odometer: endingOdometer,
        end_time: new Date().toISOString(),
        notes: tripForm.notes,
        update_vehicle_odometer: updateVehicleOdometer,
      });
      setTripForm({ truck_id: '', other_driver: '', trip_date: today(), start_time: localDateTime(), beginning_odometer: '', title: 'Trip', ending_odometer: '', notes: '' });
      setActiveTrip(null);
      setTripMode('beginning');
      setToast({ open: true, severity: 'success', message: 'Ending of trip submitted successfully.' });
      window.dispatchEvent(new Event('dataUpdated'));
      await load();
    } catch (saveError) {
      setError(saveError?.response?.data?.error || saveError?.message || 'Failed to save trip log.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRecord) return;
    try {
      if (isFuel) await driverLogService.deleteFuelLog(deleteRecord.log.id);
      else await driverLogService.deleteTripLog(deleteRecord.log.id);
      setDeleteRecord(null);
      window.dispatchEvent(new Event('dataUpdated'));
      await load();
    } catch (deleteError) {
      setDeleteRecord(null);
      setError(deleteError?.response?.data?.error || deleteError?.message || 'Failed to delete record.');
    }
  };

  return (
    <Box>
      <Card sx={{ borderRadius: 3, border: '1px solid #dbe7f3', boxShadow: 'none' }}>
        <CardContent sx={{ p: { xs: 1.5, sm: 2.5, md: 3 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
            {isFuel ? <LocalGasStationIcon color="primary" /> : <RouteIcon color="primary" />}
            <Typography variant="h6" fontWeight={900}>{isFuel ? 'Add Fuel Record' : 'Add Trip Log'}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
            {isFuel
              ? 'Record fuel added to any fleet vehicle.'
              : 'Trip logs are available for vans, service vehicles, tractors, trailers, forklifts, and skid steers.'}
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

          {isFuel ? (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2, '& > *': { minWidth: 0 } }}>
              <Box sx={{ gridColumn: '1 / -1', display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                {[
                  { value: 'biomed', label: 'Fuel filled at Biomed', helper: 'Quick internal fill-up record', color: '#e8f7ef' },
                  { value: 'outside', label: 'Fuel filled outside Biomed', helper: 'Capture receipt cost and location', color: '#fff7e8' },
                ].map((option) => (
                  <Button
                    key={option.value}
                    variant={fuelForm.fuel_source === option.value ? 'contained' : 'outlined'}
                    onClick={() => setFuelForm((form) => ({
                      ...form,
                      fuel_source: option.value,
                      station: option.value === 'biomed' ? 'Biomed' : '',
                      total_cost: option.value === 'biomed' ? '' : form.total_cost,
                    }))}
                    sx={{
                      justifyContent: 'flex-start',
                      p: 1.5,
                      minHeight: 72,
                      borderRadius: 3,
                      textAlign: 'left',
                      bgcolor: fuelForm.fuel_source === option.value ? undefined : option.color,
                    }}
                    startIcon={<LocalGasStationIcon />}
                  >
                    <Box>
                      <Typography sx={{ fontWeight: 900, lineHeight: 1.15 }}>{option.label}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.8 }}>{option.helper}</Typography>
                    </Box>
                  </Button>
                ))}
              </Box>
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', lg: 'span 3' } }}><VehicleSelect vehicles={availableVehicles} value={fuelForm.truck_id} onChange={selectFuelVehicle} label="Select Vehicle for Fuel" /></Box>
              <Box sx={{ gridColumn: { xs: '1', sm: '1 / -1', lg: 'span 3' } }}><TextField fullWidth required type="datetime-local" label="Date & Time" InputLabelProps={{ shrink: true }} value={fuelForm.fueled_at} onChange={(event) => setFuelForm((form) => ({ ...form, fueled_at: event.target.value }))} /></Box>
              <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}><TextField fullWidth type="number" label="Odometer" value={fuelForm.odometer} onChange={(event) => setFuelForm((form) => ({ ...form, odometer: event.target.value }))} /></Box>
              <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}><TextField fullWidth required type="number" label="Fuel Quantity (L)" inputProps={{ min: 0, step: '0.01' }} value={fuelForm.quantity_litres} onChange={(event) => setFuelForm((form) => ({ ...form, quantity_litres: event.target.value }))} /></Box>
              <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}>
                <FormControl fullWidth>
                  <InputLabel>Fuel Type</InputLabel>
                  <Select value={fuelForm.fuel_type} label="Fuel Type" onChange={(event) => setFuelForm((form) => ({ ...form, fuel_type: event.target.value }))}>
                    <MenuItem value="Diesel">Diesel</MenuItem>
                    <MenuItem value="Gasoline">Gasoline</MenuItem>
                    <MenuItem value="DEF">DEF</MenuItem>
                    <MenuItem value="Other">Other</MenuItem>
                  </Select>
                </FormControl>
              </Box>
              {fuelForm.fuel_source === 'outside' && (
                <>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><TextField fullWidth required type="number" label="Total Cost ($)" inputProps={{ min: 0, step: '0.01' }} value={fuelForm.total_cost} onChange={(event) => setFuelForm((form) => ({ ...form, total_cost: event.target.value }))} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><TextField fullWidth label="Station / Location" value={fuelForm.station} onChange={(event) => setFuelForm((form) => ({ ...form, station: event.target.value }))} /></Box>
                </>
              )}
              <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth multiline minRows={3} label="Notes" value={fuelForm.notes} onChange={(event) => setFuelForm((form) => ({ ...form, notes: event.target.value }))} /></Box>
              <Box sx={{ gridColumn: '1 / -1' }}><Button fullWidth variant="contained" size="large" disabled={saving || !fuelForm.truck_id || !fuelForm.quantity_litres} onClick={saveFuel} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />} sx={{ minHeight: 50 }}>Save Fuel Record</Button></Box>
            </Box>
          ) : (
            <Stack spacing={2}>
              <Alert severity={activeTrip ? 'success' : 'info'} sx={{ borderRadius: 3 }}>
                {activeTrip
                  ? <><strong>Active trip:</strong> {vehicleLabel(activeTrip.truck)} • began {formatDate(activeTrip.log.started_at, true)} at {Number(activeTrip.log.beginning_odometer).toLocaleString()} km</>
                  : 'No active trip. Save Beginning of Trip before driving.'}
              </Alert>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.5 }}>
                <Button
                  variant={tripMode === 'beginning' ? 'contained' : 'outlined'}
                  onClick={() => {
                    if (activeTrip) {
                      setTripMode('ending');
                      setError('An active trip already exists. Complete Ending of Trip first.');
                    } else {
                      setTripMode('beginning');
                      setError('');
                    }
                  }}
                  sx={{ minHeight: 76, borderRadius: 3, justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  <Box><Typography fontWeight={900}>Beginning of Trip</Typography><Typography variant="caption">Use before starting to drive.</Typography></Box>
                </Button>
                <Button
                  variant={tripMode === 'ending' ? 'contained' : 'outlined'}
                  onClick={() => { setTripMode('ending'); setError(activeTrip ? '' : 'No active trip was found. Save Beginning of Trip first.'); }}
                  sx={{ minHeight: 76, borderRadius: 3, justifyContent: 'flex-start', textAlign: 'left' }}
                >
                  <Box><Typography fontWeight={900}>Ending of Trip</Typography><Typography variant="caption">Use after finishing the trip.</Typography></Box>
                </Button>
              </Box>

              {tripMode === 'beginning' && !activeTrip ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, '& > *': { minWidth: 0 } }}>
                  <TextField fullWidth label="Name" value={user?.name || user?.email || ''} InputProps={{ readOnly: true }} />
                  <TextField fullWidth label="Other Driver (optional)" value={tripForm.other_driver} onChange={(event) => setTripForm((form) => ({ ...form, other_driver: event.target.value }))} />
                  <TextField
                    fullWidth
                    required
                    type="date"
                    label="Trip Date"
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: today() }}
                    value={tripForm.trip_date}
                    onChange={(event) => setTripForm((form) => ({
                      ...form,
                      trip_date: event.target.value,
                      start_time: dateTimeForTripDate(event.target.value, form.start_time),
                    }))}
                  />
                  <TextField
                    fullWidth
                    required
                    type="time"
                    label="Start Time"
                    InputLabelProps={{ shrink: true }}
                    value={timeValue(tripForm.start_time)}
                    onChange={(event) => setTripForm((form) => ({
                      ...form,
                      start_time: `${form.trip_date}T${event.target.value}`,
                    }))}
                  />
                  <Box sx={{ gridColumn: '1 / -1' }}><VehicleSelect vehicles={availableVehicles} value={tripForm.truck_id} onChange={selectTripVehicle} label="Select Vehicle for Beginning of Trip" /></Box>
                  <Box sx={{ gridColumn: '1 / -1' }}>
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Beginning Odometer"
                      inputProps={{ min: 0 }}
                      helperText={tripForm.truck_id ? `Latest recorded: ${Number(vehicles.find((truck) => String(truck.id) === String(tripForm.truck_id))?.odometer || 0).toLocaleString()} km (reference only; lower readings are allowed)` : 'Enter the current vehicle odometer'}
                      value={tripForm.beginning_odometer}
                      onChange={(event) => setTripForm((form) => ({ ...form, beginning_odometer: event.target.value }))}
                    />
                  </Box>
                  <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth multiline minRows={3} label="Notes (optional)" value={tripForm.notes} onChange={(event) => setTripForm((form) => ({ ...form, notes: event.target.value }))} /></Box>
                  <Box sx={{ gridColumn: '1 / -1' }}><Button fullWidth variant="contained" size="large" disabled={saving || !tripForm.truck_id || tripForm.beginning_odometer === ''} onClick={beginTrip} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SendIcon />} sx={{ minHeight: 50 }}>Save & Begin Trip</Button></Box>
                </Box>
              ) : activeTrip ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(6, minmax(0, 1fr))' }, gap: 2, '& > *': { minWidth: 0 } }}>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><TextField fullWidth label="Vehicle" value={vehicleLabel(activeTrip.truck)} InputProps={{ readOnly: true }} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><TextField fullWidth label="Trip Started" value={formatDate(activeLog.started_at, true)} InputProps={{ readOnly: true }} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}><TextField fullWidth label="Beginning Odometer" value={activeLog.beginning_odometer} InputProps={{ readOnly: true }} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}><TextField fullWidth required type="number" label="Ending Odometer" value={tripForm.ending_odometer} onChange={(event) => setTripForm((form) => ({ ...form, ending_odometer: event.target.value }))} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 2' } }}><TextField fullWidth label="Distance Travelled (km)" value={Number.isFinite(totalTrip) ? totalTrip : ''} InputProps={{ readOnly: true }} /></Box>
                  <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth required label="Title" value={tripForm.title} onChange={(event) => setTripForm((form) => ({ ...form, title: event.target.value }))} /></Box>
                  {activeLog.other_driver && <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth label="Other Driver" value={activeLog.other_driver} InputProps={{ readOnly: true }} /></Box>}
                  <Box sx={{ gridColumn: '1 / -1' }}><TextField fullWidth multiline minRows={4} label="Notes" value={tripForm.notes} onChange={(event) => setTripForm((form) => ({ ...form, notes: event.target.value }))} /></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><Button fullWidth variant="outlined" size="large" disabled={saving} onClick={() => saveTrip('draft')} startIcon={<SaveOutlinedIcon />} sx={{ minHeight: 50 }}>Save Draft</Button></Box>
                  <Box sx={{ gridColumn: { xs: '1', sm: 'span 1', lg: 'span 3' } }}><Button fullWidth variant="contained" size="large" disabled={saving || !tripForm.title || tripForm.ending_odometer === ''} onClick={() => saveTrip('completed')} startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SendIcon />} sx={{ minHeight: 50 }}>Complete Trip</Button></Box>
                </Box>
              ) : null}
            </Stack>
          )}
        </CardContent>
      </Card>

      <Box sx={{ mt: 3 }}>
        <Typography variant="h6" fontWeight={900} sx={{ mb: 2 }}>{isFuel ? 'Fuel Records' : 'Trip Log Records'}</Typography>
        {loading ? (
          <Box sx={{ py: 5, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        ) : records.length === 0 ? (
          <Alert severity="info">No {isFuel ? 'fuel records' : 'trip logs'} have been added yet.</Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(3, minmax(0, 1fr))' }, gap: 2 }}>
            {records.map((record) => (
              <Box key={record.log.id} sx={{ minWidth: 0 }}>
                {isFuel ? (
                  <RecordCard
                    title={vehicleLabel(record.truck)}
                    subtitle={`${record.log.driver_name} • ${formatDate(record.log.fueled_at, true)}`}
                    details={[
                      { label: 'Quantity', value: `${record.log.quantity_litres} L` },
                      { label: 'Filled At', value: record.log.fuel_source === 'biomed' ? 'Biomed' : record.log.fuel_source === 'outside' ? 'Outside Biomed' : '—' },
                      { label: 'Total Cost', value: record.log.total_cost ? `$${record.log.total_cost}` : '—' },
                      { label: 'Odometer', value: record.log.odometer?.toLocaleString?.() || record.log.odometer },
                      { label: 'Fuel Type', value: record.log.fuel_type },
                      { label: 'Station', value: record.log.station },
                      { label: 'Notes', value: record.log.notes },
                    ]}
                    onDelete={Number(record.log.driver_user_id) === Number(user?.id)
                      ? () => setDeleteRecord(record)
                      : null}
                  />
                ) : (
                  <RecordCard
                    title={record.log.title}
                    subtitle={`${vehicleLabel(record.truck)} • ${formatDate(record.log.trip_date)}`}
                    status={record.log.status === 'active' ? 'Active' : record.log.status === 'draft' ? 'Draft' : 'Completed'}
                    details={[
                      { label: 'Driver', value: record.log.driver_name },
                      { label: 'Other Driver', value: record.log.other_driver },
                      { label: 'Started', value: formatDate(record.log.started_at, true) },
                      { label: 'Ended', value: formatDate(record.log.ended_at, true) },
                      { label: 'Beginning', value: Number(record.log.beginning_odometer).toLocaleString() },
                      { label: 'Ending', value: record.log.ending_odometer == null ? '—' : Number(record.log.ending_odometer).toLocaleString() },
                      { label: 'Total Trip', value: record.log.total_trip == null ? 'Active' : `${Number(record.log.total_trip).toLocaleString()} km` },
                      { label: 'Notes', value: record.log.notes },
                    ]}
                    onDelete={record.log.status !== 'active' && Number(record.log.driver_user_id) === Number(user?.id)
                      ? () => setDeleteRecord(record)
                      : null}
                  />
                )}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      <Dialog open={Boolean(deleteRecord)} onClose={() => setDeleteRecord(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { m: { xs: 1.5, sm: 3 }, width: { xs: 'calc(100% - 24px)', sm: '100%' }, borderRadius: 3 } }}>
        <DialogTitle>Delete {isFuel ? 'Fuel Record' : 'Trip Log'}?</DialogTitle>
        <DialogContent>
          <Typography>The deletion is recorded in the audit log with your account and timestamp.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteRecord(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={toast.open} autoHideDuration={3500} onClose={() => setToast((value) => ({ ...value, open: false }))}>
        <Alert severity={toast.severity} onClose={() => setToast((value) => ({ ...value, open: false }))}>{toast.message}</Alert>
      </Snackbar>
    </Box>
  );
}
